import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import { thb } from '../lib/format';
import Screen from '../components/Screen';
import { calcDok } from '../components/DokCalculator';
import { useFetch } from '../lib/useFetch';
import api from '../lib/api';
import { notifyError } from '../lib/toast';
import { track } from '../lib/analytics';

export default function RecordBid() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: rounds, reload } = useFetch(`/circles/${id}/rounds`, [id]);
  const { data: circle } = useFetch(`/circles/${id}`, [id]);
  const { data: roster } = useFetch(`/circles/${id}/members`, [id]);
  const live = (rounds || []).find(r => r.status === 'live');
  const [winner, setWinner] = useState('');
  const [bid, setBid] = useState('');
  const [interest, setInterest] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const amount = circle?.amount_per_round || 0;
  const members = circle?.members || 0;
  const mode = circle?.dok_mode || 'tam';
  const isProduct = circle?.circle_kind === 'product';
  const pot = bid !== '' ? calcDok(amount, members, Number(bid), mode).receive : null;

  const submit = async () => {
    if (!live || !winner || !bid) return;
    setBusy(true);
    try {
      const potAmount = calcDok(amount, members, Number(bid), mode).receive;
      const w = (roster || []).find(m => m.user_id === winner);
      const res = await api.post(`/circles/${id}/rounds/${live.round_number}/record`, {
        winner_user_id: winner, winner_name: w?.name || '', winning_bid: Number(bid), pot_amount: potAmount,
      });
      track('round_recorded', { circle_id: id, round_number: live.round_number });
      setDone(res.data.event);
      reload();
    } catch (e) {
      notifyError(e?.response?.data?.detail || 'เฉพาะท้าวแชร์เท่านั้นที่บันทึกผลได้');
    } finally { setBusy(false); }
  };

  const drawWinner = async () => {
    if (!live) return;
    setBusy(true);
    try {
      const res = await api.post(`/circles/${id}/draw`, { interest: Number(interest) || 0 });
      track('round_drawn', { circle_id: id, round_number: live.round_number });
      setDone(res.data.event);
      reload();
    } catch (e) {
      notifyError(e?.response?.data?.detail || 'เฉพาะท้าวแชร์เท่านั้นที่จับสลากได้');
    } finally { setBusy(false); }
  };

  return (
    <Screen bg="#1a1a1a">
      <div style={{ color: '#f5f1ea', fontFamily: PS.fontUI, height: '100%', display: 'flex', flexDirection: 'column', paddingTop: 18 }}>
        <div style={{ padding: '0 20px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button data-testid="bid-back-btn" onClick={() => nav(-1)} className="ps-press" style={{ background: 'rgba(245,241,234,0.12)', border: 'none', width: 34, height: 34, borderRadius: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon.chevronL size={16} stroke="#f5f1ea" />
          </button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '5px 10px', borderRadius: 999, background: 'rgba(168,74,50,0.25)', color: 'oklch(0.75 0.12 38)', fontSize: 11, fontWeight: 600 }}>
            <div className="ps-live-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'oklch(0.75 0.12 38)' }} />{isProduct ? 'จับสลาก' : 'บันทึกผลเปีย'} · รอบ {live?.round_number || '—'}
          </div>
        </div>

        {done ? (
          <div style={{ flex: 1, padding: '0 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 16, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: 32, background: 'oklch(0.48 0.08 155)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.check size={32} stroke="#fff" sw={2.5} /></div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{isProduct ? 'จับสลากเสร็จ บันทึกหลักฐานแล้ว' : 'บันทึกผลเปียเป็นหลักฐานแล้ว'}</div>
            <div style={{ fontSize: 13, color: 'rgba(245,241,234,0.7)', lineHeight: 1.6 }}>{done.title} · {done.meta}</div>
            <div className="mono" style={{ fontSize: 11, color: 'rgba(245,241,234,0.5)', background: 'rgba(245,241,234,0.08)', padding: '8px 12px', borderRadius: 8 }}>
              <Icon.lock size={11} stroke="rgba(245,241,234,0.6)" /> {done.hash_short} · เซ็นแล้ว
            </div>
            <button data-testid="bid-view-vault-btn" onClick={() => nav(`/app/circle/${id}/vault`)} className="ps-press" style={{ marginTop: 8, height: 48, padding: '0 26px', background: 'oklch(0.72 0.12 82)', color: PS.ink, border: 'none', borderRadius: 18, fontSize: 14, fontWeight: 700, fontFamily: PS.fontUI }}>ดูในห้องนิรภัยหลักฐาน</button>
          </div>
        ) : isProduct ? (
          <>
            <div style={{ flex: 1, padding: '0 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 14 }}>
              <div style={{ width: 88, height: 88, borderRadius: 44, background: 'rgba(245,241,234,0.06)', border: '1px solid rgba(245,241,234,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon.starO size={40} stroke="oklch(0.78 0.12 82)" />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'rgba(245,241,234,0.55)', letterSpacing: 1, textTransform: 'uppercase' }}>สินค้าที่จะได้รับ</div>
                <div style={{ fontSize: 24, fontWeight: 600, marginTop: 6 }}>{circle?.product_name || circle?.name}</div>
                {circle?.product_price ? <div className="num" style={{ fontSize: 16, color: 'oklch(0.82 0.1 82)', marginTop: 4 }}>{thb(circle.product_price)}</div> : null}
              </div>
              <div style={{ fontSize: 12.5, color: 'rgba(245,241,234,0.6)', lineHeight: 1.6, maxWidth: 320 }}>
                สุ่มเลือกผู้รับรอบนี้แบบ<b style={{ color: '#f5f1ea' }}>ยุติธรรม</b> จากผู้ที่ยังไม่ได้รับ (มือตาย) — ผลสุ่มผูกกับสายโซ่หลักฐาน ตรวจสอบย้อนหลังได้ ใครก็แก้ไม่ได้
              </div>
            </div>
            <div style={{ padding: '10px 16px 16px' }}>
              {circle?.interest_enabled && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(245,241,234,0.6)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6, textAlign: 'left' }}>ดอกที่ผู้รับรอบนี้จ่ายเพิ่ม (บาท)</div>
                  <input data-testid="draw-interest-input" type="number" value={interest} onChange={e => setInterest(e.target.value)} placeholder="0 = ไม่เก็บดอกรอบนี้" className="num"
                    style={{ width: '100%', height: 46, borderRadius: 12, background: 'rgba(245,241,234,0.08)', border: '1px solid rgba(245,241,234,0.14)', color: PS.page, padding: '0 14px', fontSize: 16, outline: 'none' }} />
                  <div style={{ fontSize: 10, color: 'rgba(245,241,234,0.45)', marginTop: 6, textAlign: 'left' }}>คนรับก่อนมักจ่ายดอกมาก คนรับท้ายจ่ายน้อย/ได้ส่วนลด — บันทึกเป็นหลักฐาน</div>
                </div>
              )}
              <button data-testid="draw-submit-btn" disabled={busy || !live} onClick={drawWinner} className="ps-press"
                style={{ width: '100%', height: 56, background: live ? 'oklch(0.72 0.12 82)' : 'rgba(245,241,234,0.15)', color: PS.ink, border: 'none', borderRadius: 18, fontSize: 15, fontWeight: 700, opacity: busy ? 0.6 : 1, fontFamily: PS.fontUI }}>
                {busy ? 'กำลังสุ่ม…' : 'จับสลากผู้รับรอบนี้'}
              </button>
              <div style={{ fontSize: 10.5, color: 'rgba(245,241,234,0.45)', textAlign: 'center', marginTop: 10, lineHeight: 1.5 }}>
                Peershare บันทึกผลการสุ่มเป็นหลักฐานที่ตรวจสอบได้ — ไม่ถือเงิน ไม่ถือสินค้า
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ flex: 1, padding: '0 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'rgba(245,241,234,0.55)', letterSpacing: 1, textTransform: 'uppercase' }}>ยอดรับของผู้ชนะ (ประมาณ)</div>
                <div className="num" style={{ fontSize: 64, fontWeight: 500, lineHeight: 1, marginTop: 8, letterSpacing: -2 }}>{pot != null ? thb(pot) : '฿—'}</div>
                <div style={{ fontSize: 12.5, color: 'rgba(245,241,234,0.6)', marginTop: 8 }}>คิดจาก ฿{amount.toLocaleString()} × {members} คน − ดอกเปีย</div>
              </div>

              <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(245,241,234,0.55)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>ผู้ชนะเปียรอบนี้</div>
                  <div style={{ position: 'relative' }}>
                    <select data-testid="bid-winner-select" value={winner} onChange={e => setWinner(e.target.value)}
                      style={{ width: '100%', height: 52, borderRadius: 16, background: 'rgba(245,241,234,0.08)', border: '1px solid rgba(245,241,234,0.12)', color: winner ? '#f5f1ea' : 'rgba(245,241,234,0.5)', padding: '0 40px 0 16px', fontSize: 15, fontFamily: PS.fontUI, outline: 'none', appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}>
                      <option value="" disabled style={{ color: '#1a1a1a' }}>เลือกสมาชิกผู้ชนะ…</option>
                      {(roster || []).filter(m => m.status !== 'exited').map(m => (
                        <option key={m.user_id} value={m.user_id} disabled={m.pay_status === 'won'} style={{ color: '#1a1a1a' }}>
                          {`${m.name}${m.role === 'host' ? ' (ท้าวแชร์)' : ''}${m.pay_status === 'won' ? ' · รับแล้ว' : ''}`}
                        </option>
                      ))}
                    </select>
                    <div style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                      <Icon.chevronD size={18} stroke="rgba(245,241,234,0.6)" />
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'rgba(245,241,234,0.55)', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>ดอกเปีย (บาท)</div>
                  <input data-testid="bid-amount-input" type="number" value={bid} onChange={e => setBid(e.target.value)} placeholder="850"
                    className="num" style={{ width: '100%', height: 52, borderRadius: 16, background: 'rgba(245,241,234,0.08)', border: '1px solid rgba(245,241,234,0.12)', color: '#f5f1ea', padding: '0 16px', fontSize: 18, outline: 'none' }} />
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    {[650, 800, 850, 1000].map(v => (
                      <button key={v} data-testid={`bid-quick-${v}`} onClick={() => setBid(String(v))} className="ps-press num" style={{ flex: 1, height: 40, borderRadius: 12, background: 'rgba(245,241,234,0.08)', border: 'none', color: '#f5f1ea', fontSize: 14 }}>฿{v}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: '10px 16px 16px' }}>
              <button data-testid="bid-submit-btn" disabled={busy || !winner || !bid || !live} onClick={submit} className="ps-press"
                style={{ width: '100%', height: 56, background: live ? 'oklch(0.72 0.12 82)' : 'rgba(245,241,234,0.15)', color: PS.ink, border: 'none', borderRadius: 18, fontSize: 15, fontWeight: 700, letterSpacing: 0.2, opacity: (busy || !winner || !bid) ? 0.6 : 1, fontFamily: PS.fontUI }}>
                {busy ? 'กำลังบันทึก…' : 'บันทึกผลเป็นหลักฐาน'}
              </button>
              {(!winner || !bid) && live && (
                <div data-testid="bid-disabled-hint" style={{ fontSize: 11, color: 'oklch(0.82 0.1 82)', textAlign: 'center', marginTop: 8 }}>
                  {!winner ? 'เลือกผู้ชนะเปียก่อน' : 'กรอกจำนวนดอกเปียก่อนบันทึก'}
                </div>
              )}
              <div style={{ fontSize: 10.5, color: 'rgba(245,241,234,0.45)', textAlign: 'center', marginTop: 10, lineHeight: 1.5 }}>
                การประมูลเกิดขึ้นจริงในวง — Peershare เพียงบันทึกผลนี้เป็นหลักฐานที่แก้ไขไม่ได้
              </div>
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
